import { useState } from 'react';
import { Database, Plus, Link as LinkIcon, FileText, Trash2 } from 'lucide-react';
import UploadFileMock from './UploadFileMock';
/**
 * Formata tamanho em bytes para exibição.
 */
function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Base de conhecimento (RAG): lista de fontes (URL e arquivo), adicionar e remover.
 * Upload mockado; estrutura preparada para { id, type, name, size?, createdAt }.
 *
 * @param {Object} props
 * @param {{ id: string, name?: string, type: string, size?: number, createdAt?: string, url?: string }[]} props.sources
 * @param {(source: { type: 'url'|'file', name: string, size?: number, url?: string }) => void} props.onAddSource
 * @param {(sourceId: string) => void} props.onRemoveSource
 */
export default function AgentKnowledgeBase({ sources = [], onAddSource, onRemoveSource }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState('');

  const handleAddUrl = (e) => {
    e.preventDefault();
    const url = newUrl.trim();
    if (!url) return;
    onAddSource({ type: 'url', name: url, url });
    setNewUrl('');
    setShowAdd(false);
  };

  const handleFileMock = (file) => {
    onAddSource({
      type: 'file',
      name: file.name,
      size: file.size,
    });
  };

  return (
    <div className="bg-appSurface p-8 rounded-3xl border border-appSurfaceBorder shadow-sm space-y-6">
      <h3 className="text-xs font-black text-appText uppercase tracking-widest flex items-center gap-2">
        <Database size={16} className="text-appPrimary" /> Base de Conhecimento (RAG)
      </h3>

      <div className="space-y-2">
        {sources.length === 0 ? (
          <div className="p-8 rounded-2xl border-2 border-dashed border-appSurfaceBorder text-center text-appTextMuted text-sm">
            Nenhuma fonte adicionada. Use o botão abaixo para adicionar URL ou arquivo.
          </div>
        ) : (
          sources.map((source) => (
            <div
              key={source.id}
              className="flex items-center justify-between p-3.5 bg-appSurfaceBorder/20 rounded-xl border border-appSurfaceBorder group"
            >
              <div className="flex items-center gap-3 min-w-0">
                {source.type === 'url' ? (
                  <LinkIcon size={14} className="text-blue-500 shrink-0" />
                ) : (
                  <FileText size={14} className="text-rose-500 shrink-0" />
                )}
                <div className="min-w-0">
                  <span className="text-xs font-medium text-appText truncate block">
                    {source.name || source.url || '—'}
                  </span>
                  {source.size != null && (
                    <span className="text-[10px] text-appTextMuted">{formatSize(source.size)}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRemoveSource(source.id)}
                className="p-1.5 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all shrink-0"
                aria-label="Remover fonte"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="space-y-3">
        {showAdd ? (
          <form onSubmit={handleAddUrl} className="flex gap-2 flex-wrap">
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://..."
              className="flex-1 min-w-[200px] px-3 py-2 rounded-xl border border-appSurfaceBorder bg-appSurface text-appText text-sm focus:ring-2 focus:ring-appPrimary outline-none"
              aria-label="URL da fonte"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-appPrimary text-white rounded-xl text-xs font-bold hover:opacity-90"
            >
              Adicionar
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setNewUrl(''); }}
              className="px-4 py-2 border border-appSurfaceBorder rounded-xl text-xs font-bold text-appText hover:bg-appSurfaceBorder/20"
            >
              Cancelar
            </button>
          </form>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 py-3 px-4 border-2 border-dashed border-appSurfaceBorder rounded-2xl text-[10px] font-black uppercase tracking-widest text-appTextMuted hover:border-appPrimary hover:text-appPrimary transition-all"
            >
              <LinkIcon size={14} /> Adicionar URL
            </button>
            <UploadFileMock onFileMock={handleFileMock} label="Adicionar arquivo" />
          </div>
        )}
      </div>
    </div>
  );
}
